/**
 * KioskWatchdog — shared idle / blocking-dialog state for the unattended
 * exhibition build.
 *
 * It only holds state. The actual timer that decides when to force a "total
 * reset" (full page reload) lives in `main.ts`, because that decision also
 * needs the live scene list and the debounced `safeReload`. Scenes and modals
 * feed this object so the watchdog can tell an abandoned screen apart from one
 * the player is actively using.
 *
 * Scope (see main.ts): the reset fires on a stuck dialog or an idle static
 * screen (game over, stage clear, settings, controls, leaderboard, boss
 * victory). Active gameplay, the main menu (which runs its own attract demo)
 * and the credits crawl are intentionally exempt.
 */
export class KioskWatchdog {
  private static _instance: KioskWatchdog;

  static get instance(): KioskWatchdog {
    if (!this._instance) this._instance = new KioskWatchdog();
    return this._instance;
  }

  /** Epoch ms of the last real user input (mouse / keyboard / gamepad). */
  lastActivity = Date.now();

  /** True while a boss-victory card is showing (a static, abandonable screen). */
  bossVictory = false;

  /** Count of currently open blocking dialogs. */
  private modals = 0;

  /** Call on any real user input to keep the screen "alive". */
  noteActivity(): void {
    this.lastActivity = Date.now();
  }

  /** A blocking dialog opened. */
  pushModal(): void {
    this.modals += 1;
    this.lastActivity = Date.now();
  }

  /** A blocking dialog closed. */
  popModal(): void {
    this.modals = Math.max(0, this.modals - 1);
    this.lastActivity = Date.now();
  }

  get modalOpen(): boolean {
    return this.modals > 0;
  }

  setBossVictory(showing: boolean): void {
    this.bossVictory = showing;
    this.lastActivity = Date.now();
  }

  /**
   * Clear all transient kiosk state. Used by the soft reset to attract so a new
   * session starts as if freshly booted — no stale modal count, no lingering
   * boss-victory flag — without paying for a full page reload.
   */
  reset(): void {
    this.modals = 0;
    this.bossVictory = false;
    this.lastActivity = Date.now();
  }
}
